import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, AlertTriangle, CheckCircle2, Mail } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import PasswordField from "@/components/auth/PasswordField";
import Button from "@/components/ui/Button";
import { checkPasswordReset, resetPassword } from "@/api/auth";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [info, setInfo] = useState(null); // { valid, email }
  const [password, setPassword] = useState("");
  const [error, setError] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const redirectTimer = useRef(null);
  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setChecking(false); setInfo({ valid: false }); return undefined; }
    checkPasswordReset(token)
      .then((res) => { if (!cancelled) setInfo(res); })
      .catch(() => { if (!cancelled) setInfo({ valid: false }); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) { setError("Use at least 8 characters."); return; }
    setError(undefined);
    setSubmitting(true);
    try {
      await resetPassword({ token, new_password: password });
      setDone(true);
      // Tracked so the redirect can be cancelled if the user leaves first —
      // otherwise it fires against an unmounted component.
      redirectTimer.current = setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(err.message || "Could not reset your password.");
      setSubmitting(false);
    }
  }

  // ---- loading -------------------------------------------------------------
  if (checking) {
    return (
      <AuthLayout title="Checking your link…" subtitle="One moment.">
        <div className="flex items-center justify-center py-10 text-slate-400">
          <Loader2 size={26} className="animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  // ---- invalid / expired ---------------------------------------------------
  if (!info || !info.valid) {
    return (
      <AuthLayout
        title="Link not valid"
        subtitle="This password-reset link is invalid or has expired."
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p>Reset links expire an hour after they&apos;re sent. Request a fresh one to continue.</p>
          </div>
          {/* Button already renders a router Link when given `to`. Nesting a
              <button> inside an <a> is invalid HTML. */}
          <Button to="/forgot-password" className="w-full">
            Request a new link <ArrowRight size={18} />
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // ---- success -------------------------------------------------------------
  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="You can now sign in with your new password.">
        <div className="flex flex-col items-center rounded-2xl border border-emerald-200 bg-emerald-50/60 p-8 text-center dark:border-emerald-500/20 dark:bg-emerald-500/10">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500 text-white">
            <CheckCircle2 size={26} />
          </span>
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
            Taking you to sign in…
          </p>
        </div>
      </AuthLayout>
    );
  }

  // ---- reset form ----------------------------------------------------------
  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Pick a strong password you don't use elsewhere."
    >
      {info.email ? (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50/70 p-4 dark:border-brand-500/20 dark:bg-brand-500/10">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/15 text-brand-600 dark:text-brand-300">
            <Mail size={20} />
          </span>
          <div className="min-w-0 text-sm">
            <p className="truncate text-slate-500 dark:text-slate-400">Resetting the password for</p>
            <p className="truncate font-semibold text-slate-900 dark:text-white">{info.email}</p>
          </div>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <PasswordField
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(undefined); }}
          error={error}
          showStrength
          placeholder="New password"
        />
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? (
            <><Loader2 size={18} className="animate-spin" /> Updating your password…</>
          ) : (
            <><CheckCircle2 size={18} /> Reset password</>
          )}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        Remembered it?{" "}
        <Link to="/login" className="font-semibold text-brand-600 hover:text-brand-500 dark:text-brand-400">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
